import { useState, useEffect } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import SideBar from '../../components/global/SideBar';
import { useCurrentUser } from '@beholos/holoscommon';
import './index.css';
import { getYearInReviewData } from '../../services/user.service';

export default function YearInReview() {
  usePageTitle('Resumen del Año - Holos');

  const { user } = useCurrentUser();

  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const year = 2025; // or detect automatically

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getYearInReviewData(year);
        setReviewData(data);
      } catch (err: any) {
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchData();
  }, [user, year]);

  if (!user) return null;

  if (loading)
    return (
      <div className="flex h-screen items-center justify-center text-xl">
        Cargando resumen del año...
      </div>
    );

  if (error || !reviewData)
    return (
      <div className="flex h-screen items-center justify-center text-red-500">
        Error cargando resumen: {error}
      </div>
    );

  const {
    classYear,
    studios = [],
    favoriteCategory,
    totalMinutes,
    newStudioYear,
    rank,
    caloriesYear,
    levelName,
    levelImage,
    fitPoints,
    mostClassesMonth,
    mostClassesNumberMonth,
    favoriteCategories = []
  } = reviewData || {};

  return (
    <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
      <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
        <SideBar />
      </div>

      <div className="flex-1 h-full overflow-y-auto bg-gray-200 py-10 px-6">
        <div className="bg-white p-6 rounded max-w-5xl mx-auto border border-gray-200 shadow-md">
          <div className="mx-auto w-[90%] max-w-3xl text-center">

            {/* Header */}
            <img
              src="https://cache.beholos.com/img/emails/holoslogo001.png"
              alt="Holos Logo"
              className="mx-auto w-52 mb-2"
            />
            <p className="text-gray-600">
              {user?.firstname}, Así has aprovechado tu tiempo con Holos este {year}:
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="border p-4 rounded text-center bg-white">
                <img
                  src="https://image.beholos.com/img/holos/emails/yearEndReview/calendar02.jpg"
                  className="h-20 mx-auto"
                />
                <div className="text-holos-dark text-2xl font-bold">{classYear}</div>
                <p className="text-gray-500">reservas asistidas</p>
              </div>

              {studios?.length > 0 && (
                <div className="border p-4 rounded text-center bg-white">
                  <img
                    src="https://image.beholos.com/img/holos/emails/yearEndReview/ranking04.jpg"
                    className="h-20 mx-auto"
                  />
                  <div className="text-holos-dark text-2xl font-bold">
                    {studios[0].name}
                  </div>
                  <p className="text-gray-500">partner favorito</p>
                </div>
              )}
            </div>

            {/* Summary 1 */}
            <div className="flex justify-between mt-6">
              <div className="text-center flex-1">
                <img
                  src="https://image.beholos.com/img/holos/emails/yearEndReview/dis01.jpg"
                  className="w-12 h-12 mx-auto"
                />
                <h3 className="text-holos-dark text-xl mt-2">{favoriteCategory}</h3>
                <p className="text-gray-500">disciplina favorita</p>
              </div>

              <div className="text-center flex-1">
                <img
                  src="https://image.beholos.com/img/holos/emails/yearEndReview/tiempo06.jpg"
                  className="w-12 h-12 mx-auto"
                />
                <h3 className="text-holos-dark text-xl mt-2">{totalMinutes}</h3>
                <p className="text-gray-500">minutos activos</p>
              </div>

              <div className="text-center flex-1">
                <img
                  src="https://image.beholos.com/img/holos/emails/yearEndReview/partner03.jpg"
                  className="w-12 h-12 mx-auto"
                />
                <h3 className="text-holos-dark text-xl mt-2">{newStudioYear}</h3>
                <p className="text-gray-500">partners visitados</p>
              </div>
            </div>

            {/* Summary 2 */}
            <div className="flex justify-between mt-6">
              <div className="text-center flex-1">
                <img
                  src="https://image.beholos.com/img/holos/emails/yearEndReview/ranking04.jpg"
                  className="w-12 h-12 mx-auto"
                />
                <h3 className="text-holos-dark text-xl mt-2">#{rank}</h3>
                <p className="text-gray-500">de todos los Holers</p>
              </div>

              <div className="text-center flex-1">
                <img
                  src="https://image.beholos.com/img/holos/emails/yearEndReview/caloria06.png"
                  className="w-12 h-12 mx-auto"
                />
                <h3 className="text-holos-dark text-xl mt-2">{caloriesYear}</h3>
                <p className="text-gray-500">calorías quemadas</p>
              </div>

              <div className="text-center flex-1">
                {levelName && (
                  <>
                    <img src={levelImage} className="w-12 h-12 mx-auto" />
                    <h3 className="text-holos-dark text-xl mt-2">{levelName}</h3>
                    <div className="font-bold text-holos-dark">{fitPoints} HoloPoints</div>
                    <p className="text-gray-500">Nivel actual</p>
                  </>
                )}
              </div>
            </div>

            {/* Favorite studios + categories */}
            <div className="mt-8">
              <p className="text-xl font-semibold text-center">
                Tu mes más activo fue <span className="text-holos-dark">{mostClassesMonth}</span>, fuiste a{' '}
                <span className="text-holos-dark">{mostClassesNumberMonth}</span> clases ese mes.
              </p>

              {/* Other favs */}
              <div id="favorites" className="mt-6 text-left">
                <p className="font-bold mb-2 text-center">Otros partners favoritos</p>
                <ol className="list-decimal ml-6">
                  {studios?.slice(1).map((studio: any, idx: number) => (
                    <li key={idx} className="text-black">
                      {studio.name}
                    </li>
                  ))}
                </ol>

                <p className="font-bold mt-6 mb-2 text-center">Otras actividades favoritas</p>
                <ol className="list-decimal ml-6">
                  {favoriteCategories?.slice(1).map((cat: any, idx: number) => (
                    <li key={idx} className="text-black">
                      {cat.name}
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center mt-8 text-xl text-gray-700">
              Estamos orgullosos de lo que lograste
              <br />
              ¡Vamos por más en este nuevo año!
            </div>
            <h2 className="text-3xl text-center mt-4 font-bold">¿Estás listo para el 2026?</h2>
          </div>
        </div>
      </div>
    </div>
  );
}
